// The loop client (Part A, 3 Nov 2026): the profile and the boards over the account session (staffApi). Every reply is
// checked for shape; a body that is not what it should be is refused, never half-rendered.
import type { BoardRow, BoardView, ProfileUpdate, ProfileView } from '@shared/types';
import { staffApi } from '@/staff/auth';

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const str = (v: unknown): v is string => typeof v === 'string';
const obj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

export function boardRow(raw: unknown): BoardRow | undefined {
  if (!obj(raw) || !str(raw.name) || !num(raw.points)) return undefined;
  return {
    name: raw.name, points: raw.points, battles: num(raw.battles) ? raw.battles : 0, verified: raw.verified === true,
    ...(num(raw.place) ? { place: raw.place } : {}), ...(str(raw.university) ? { university: raw.university } : {}),
    ...(num(raw.streak) ? { streak: raw.streak } : {}), ...(raw.you === true ? { you: true as const } : {}),
  };
}

export function boardView(raw: unknown): BoardView | undefined {
  if (!obj(raw) || !str(raw.week) || !num(raw.start) || !num(raw.end) || !Array.isArray(raw.rows)) return undefined;
  if (raw.board !== 'global' && raw.board !== 'university' && raw.board !== 'friends') return undefined;
  const rows = raw.rows.map(boardRow).filter((r): r is BoardRow => !!r);
  const you = boardRow(raw.you);
  return { board: raw.board, week: raw.week, start: raw.start, end: raw.end, rows, total: num(raw.total) ? raw.total : rows.length, ...(you ? { you } : {}) };
}

export function profileView(raw: unknown): ProfileView | undefined {
  if (!obj(raw) || !str(raw.email) || !str(raw.displayName) || !obj(raw.week) || !str(raw.week.key) || !obj(raw.streak)) return undefined;
  const w = raw.week; const key = w.key as string; const s = raw.streak; const b = raw.badge; const t = raw.topic; const l = raw.friendLink;
  return {
    email: raw.email, displayName: raw.displayName, nameIsDefault: raw.nameIsDefault === true, canChangeName: raw.canChangeName === true,
    timeZone: str(raw.timeZone) ? raw.timeZone : 'Europe/London',
    ...(obj(b) && (b.kind === 'university' || b.kind === 'nhs') ? { badge: { kind: b.kind, ...(str(b.university) ? { university: b.university } : {}) } } : {}),
    streak: { current: num(s.current) ? s.current : 0, best: num(s.best) ? s.best : 0 },
    ...(num(raw.accuracy) ? { accuracy: raw.accuracy } : {}), battles: num(raw.battles) ? raw.battles : 0, podiums: num(raw.podiums) ? raw.podiums : 0,
    ...(num(raw.bestPlace) ? { bestPlace: raw.bestPlace } : {}),
    week: { key, start: num(w.start) ? w.start : 0, end: num(w.end) ? w.end : 0, points: num(w.points) ? w.points : 0, battles: num(w.battles) ? w.battles : 0, ...(num(w.place) ? { place: w.place } : {}) },
    ranks: (Array.isArray(raw.ranks) ? raw.ranks : []).filter((r): r is { week: string; place: number; points: number } => obj(r) && str(r.week) && num(r.place) && num(r.points)).map((r) => ({ week: r.week, place: r.place, points: r.points })),
    ...(obj(t) && str(t.title) ? { topic: { title: t.title } } : {}),
    ...(obj(l) && str(l.token) ? { friendLink: { token: l.token } } : {}),
    requests: num(raw.requests) ? raw.requests : 0,
  };
}

const checked = <T,>(parse: (raw: unknown) => T | undefined, what: string) => (raw: unknown): T => {
  const v = parse(raw);
  if (!v) throw Object.assign(new Error(`The ${what} did not come back as expected.`), { name: 'BadReply' });
  return v;
};

// Friends (Task 14, 4 Nov 2026): ids are hashes and names display names; no reply names anyone's address.
export interface Friend { id: string; name: string; since: number }
export interface FriendRequest { id: string; name: string; at: number; code: string }
export interface FriendsView { friends: Friend[]; requests: FriendRequest[] }

export function friendsView(raw: unknown): FriendsView | undefined {
  if (!obj(raw) || !Array.isArray(raw.friends) || !Array.isArray(raw.requests)) return undefined;
  const friends = raw.friends.filter((f): f is Friend => obj(f) && str(f.id) && str(f.name)).map((f) => ({ id: f.id, name: f.name, since: num(f.since) ? f.since : 0 }));
  const requests = raw.requests.filter((r): r is FriendRequest => obj(r) && str(r.id) && str(r.name)).map((r) => ({ id: r.id, name: r.name, at: num(r.at) ? r.at : 0, code: str(r.code) ? r.code : '' }));
  return { friends, requests };
}
const named = (raw: unknown): { name: string } | undefined => (obj(raw) && str(raw.name) ? { name: raw.name } : undefined);
const tokened = (raw: unknown): { token: string } | undefined => (obj(raw) && str(raw.token) ? { token: raw.token } : undefined);

/** The address a friend link points at: this app, /f/<token>. */
export const friendUrl = (token: string, origin: string = window.location.origin) => `${origin}/f/${encodeURIComponent(token)}`;

export interface LoopClient {
  profile(): Promise<ProfileView>;
  updateProfile(u: ProfileUpdate): Promise<ProfileView>;
  board(kind: BoardView['board'], week: 'this' | 'last'): Promise<BoardView>;
  friends(): Promise<FriendsView>;
  addByToken(token: string): Promise<{ name: string }>;
  addByShare(shareTag: string): Promise<{ name: string }>;
  removeFriend(id: string): Promise<void>;
  link(): Promise<{ token: string }>;
  newLink(): Promise<{ token: string }>;
  linkOwner(token: string): Promise<{ name: string }>;
  answerRequest(id: string, accept: boolean): Promise<void>;
}

export function loopClient(session: string): LoopClient {
  const auth = { session };
  const asProfile = checked(profileView, 'profile'); const asBoard = checked(boardView, 'board');
  const asFriends = checked(friendsView, 'friends'); const asNamed = checked(named, 'friend'); const asToken = checked(tokened, 'friend link');
  const post = (body: unknown) => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  return {
    profile: async () => asProfile(await staffApi<unknown>('/profile', auth)),
    updateProfile: async (u) => asProfile(await staffApi<unknown>('/profile', auth, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(u) })),
    board: async (kind, week) => asBoard(await staffApi<unknown>(`/boards/${kind}?week=${week}`, auth)),
    friends: async () => asFriends(await staffApi<unknown>('/friends', auth)),
    addByToken: async (token) => asNamed(await staffApi<unknown>('/friends', auth, post({ token }))),
    addByShare: async (shareTag) => asNamed(await staffApi<unknown>('/friends', auth, post({ shareTag }))),
    removeFriend: async (id) => { await staffApi<unknown>(`/friends/${encodeURIComponent(id)}`, auth, { method: 'DELETE' }); },
    link: async () => asToken(await staffApi<unknown>('/friends/link', auth)),
    newLink: async () => asToken(await staffApi<unknown>('/friends/link', auth, { method: 'POST' })),
    linkOwner: async (token) => asNamed(await staffApi<unknown>(`/friends/link/${encodeURIComponent(token)}`, auth)),
    answerRequest: async (id, accept) => { await staffApi<unknown>(`/friends/requests/${encodeURIComponent(id)}`, auth, post({ accept })); },
  };
}
